import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import { useAuthStore } from '@/stores/authStore'
import { deviceService } from '@/services/device.service'
import { useToast } from '@/components/ui/use-toast'
import { formatUTCToLocal } from '@/utils/date'
import { KeyRound, Trash2 } from 'lucide-react'

interface ClaimCode {
  id: string
  name: string
  serial_number: string
  device_type: string
  claim_token: string
  status: string
  account_id: string | null
  created_at: string | null
}

export const DeviceClaimCodesPage = () => {
  const { user } = useAuthStore()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const { data: claimCodes, isLoading, error } = useQuery<ClaimCode[]>({
    queryKey: ['device-claim-codes'],
    queryFn: () => deviceService.getClaimCodes(),
    staleTime: 30000,
    enabled: !!user?.isSuperUser,
  })

  const generateMutation = useMutation({
    mutationFn: (deviceId: string) => deviceService.generateClaimToken(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-claim-codes'] })
      toast({ title: 'Claim token generated successfully' })
    },
    onError: () => {
      toast({ title: 'Failed to generate claim token', variant: 'destructive' })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (deviceId: string) => deviceService.revokeClaimToken(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-claim-codes'] })
      toast({ title: 'Claim token revoked successfully' })
    },
    onError: () => {
      toast({ title: 'Failed to revoke claim token', variant: 'destructive' })
    },
  })

  if (!user?.isSuperUser) {
    return <Navigate to="/unauthorized" replace />
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error) {
    return <ErrorMessage message="Failed to load claim codes. Please try again." />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold">Device Claim Codes</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Active Claim Tokens
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device Name</TableHead>
                  <TableHead>Serial Number</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Claim Token</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {claimCodes && claimCodes.length > 0 ? (
                  claimCodes.map((device) => (
                    <TableRow key={device.id}>
                      <TableCell className="font-medium">{device.name}</TableCell>
                      <TableCell className="font-mono text-sm">{device.serial_number}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{device.device_type}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm max-w-[200px] truncate">
                        {device.claim_token}
                      </TableCell>
                      <TableCell>
                        <Badge variant={device.status === 'ACTIVE' ? 'default' : 'secondary'}>
                          {device.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {device.created_at ? formatUTCToLocal(device.created_at) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => generateMutation.mutate(device.id)}
                            disabled={generateMutation.isPending}
                          >
                            <KeyRound className="mr-1 h-3 w-3" />
                            Generate
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => revokeMutation.mutate(device.id)}
                            disabled={revokeMutation.isPending}
                          >
                            <Trash2 className="mr-1 h-3 w-3" />
                            Revoke
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No active claim codes found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
